import { useCallback, useEffect, useState } from "react";

import { api, ApiClientError } from "../api.js";
import { DataTable } from "../components/data-table.js";
import { MetricCard } from "../components/metric-card.js";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  PeriodSelector
} from "../components/state.js";
import { TrendChart } from "../components/trend-chart.js";
import {
  formatBytes,
  formatDate,
  formatNumber,
  formatSpeed,
  last24HourSeries,
  resultLabel,
  shortId
} from "../format.js";
import type {
  DownloadList,
  DownloadSummary,
  Overview,
  QueryPeriod
} from "../types.js";

export const DOWNLOAD_RESULT_OPTIONS = [
  { value: "in_progress", label: "ЗАГРУЗКА" },
  { value: "success", label: "УСПЕХ" },
  { value: "failed", label: "СБОЙ" },
  { value: "cancelled", label: "ОТМЕНА" },
  { value: "interrupted", label: "ПРЕРВАНА" }
] as const;

export function DownloadResultBand({
  summary
}: {
  summary: DownloadSummary;
}) {
  return (
    <section className="result-band">
      <div>
        <span className="status-dot" />
        ЗАГРУЗКА <strong>{summary.in_progress}</strong>
      </div>
      <div>
        <span className="status-dot status-dot--good" />
        УСПЕХ <strong>{summary.success}</strong>
      </div>
      <div>
        <span className="status-dot status-dot--bad" />
        СБОЙ <strong>{summary.failed}</strong>
      </div>
      <div>
        <span className="status-dot" />
        ОТМЕНА <strong>{summary.cancelled}</strong>
      </div>
      <div>
        <span className="status-dot" />
        ПРЕРВАНА <strong>{summary.interrupted}</strong>
      </div>
    </section>
  );
}

export function DownloadsPage({
  onUnauthorized
}: {
  onUnauthorized(): void;
}) {
  const [period, setPeriod] = useState<QueryPeriod>({ period: "24h" });
  const [version, setVersion] = useState("");
  const [result, setResult] = useState("");
  const [installationType, setInstallationType] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<DownloadList | null>(null);
  const [trend, setTrend] = useState<Overview | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const [downloadResult, trendResult] = await Promise.all([
        api.downloads(period, {
          launcherVersion: version,
          result,
          installationType,
          limit: 50,
          offset
        }),
        api.overview({ period: "24h" }, version)
      ]);
      setData(downloadResult);
      setTrend(trendResult);
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 401) {
        onUnauthorized();
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [installationType, offset, onUnauthorized, period, result, version]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!data && !error) {
    return <LoadingState label="ЧТЕНИЕ ЗАГРУЗОК" />;
  }
  if (!data && error) {
    return <ErrorState message={error} onRetry={() => void load()} />;
  }
  if (!data) {
    return null;
  }
  const speedSeries = last24HourSeries(
    trend?.trend ?? [],
    (point) => (point.mean_speed_bps ?? 0) / (1024 * 1024)
  );
  return (
    <div className="page-stack">
      <section className="toolbar toolbar--wrap">
        <PeriodSelector
          value={period}
          onChange={(value) => {
            setOffset(0);
            setPeriod(value);
          }}
        />
        <label className="field field--compact">
          <span>ВЕРСИЯ</span>
          <input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="ВСЕ"
          />
        </label>
        <label className="field field--compact">
          <span>РЕЗУЛЬТАТ</span>
          <select
            value={result}
            onChange={(event) => setResult(event.target.value)}
          >
            <option value="">ВСЕ</option>
            {DOWNLOAD_RESULT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field field--compact">
          <span>СТАТУС ЛИЦЕНЗИИ</span>
          <select
            value={installationType}
            onChange={(event) => setInstallationType(event.target.value)}
          >
            <option value="">ВСЕ</option>
            <option value="licensed">ЛИЦЕНЗИЯ</option>
            <option value="pirated">ПИРАТКА</option>
            <option value="unknown">НЕИЗВЕСТНО</option>
          </select>
        </label>
      </section>
      <section className="metric-grid metric-grid--four">
        <MetricCard
          label="ПЕРЕДАНО"
          value={formatBytes(data.summary.bytes)}
          note={`${formatNumber(data.summary.sessions)} ПОПЫТОК`}
        />
        <MetricCard
          label="ФАЙЛЫ"
          value={formatNumber(data.summary.files)}
          note="ЗАВЕРШЁННЫЕ СОБЫТИЯ"
        />
        <MetricCard
          label="СРЕДНЯЯ СКОРОСТЬ"
          value={formatSpeed(data.summary.mean_speed_bps)}
          note="БАЙТЫ / ОБЩЕЕ ВРЕМЯ"
          tone="good"
        />
        <MetricCard
          label="МЕДИАННАЯ СКОРОСТЬ"
          value={formatSpeed(data.summary.median_speed_bps)}
          note="ПО ПОПЫТКАМ"
        />
      </section>
      <article className="panel">
        <header className="panel__header">
          <div>
            <h2>СРЕДНЯЯ СКОРОСТЬ / ПОСЛЕДНИЕ 24 ЧАСА</h2>
          </div>
        </header>
        <TrendChart
          values={speedSeries.values}
          pointLabels={speedSeries.labels}
          label="СРЕДНЯЯ СКОРОСТЬ"
          unitLabel="СРЕДНЯЯ СКОРОСТЬ"
          valueFormatter={(value) =>
            `${value.toLocaleString("ru-RU", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1
            })} МБ/С`
          }
        />
      </article>
      <DownloadResultBand summary={data.summary} />
      <article className="panel">
        <header className="panel__header">
          <div>
            <h2>ПОПЫТКИ ЗАГРУЗКИ</h2>
          </div>
          <span className="panel__meta">{data.total} ВСЕГО</span>
        </header>
        {data.items.length ? (
          <>
            <DataTable
              headings={[
                "ВРЕМЯ",
                "КЛИЕНТ",
                "ВЕРСИЯ / ЛИЦЕНЗИЯ",
                "РЕЗУЛЬТАТ",
                "ДАННЫЕ",
                "ФАЙЛЫ",
                "СКОРОСТЬ"
              ]}
            >
              {data.items.map((item) => (
                <tr key={item.id}>
                  <td className="mono">{formatDate(item.effective_at)}</td>
                  <td>
                    <code title={item.installation_id}>
                      {shortId(item.installation_id)}
                    </code>
                  </td>
                  <td>
                    <strong className="mono">{item.launcher_version}</strong>
                    <small>{resultLabel(item.installation_type)}</small>
                  </td>
                  <td>
                    <span className={`tag tag--${item.result}`}>
                      {resultLabel(item.result)}
                    </span>
                  </td>
                  <td className="mono">
                    {formatBytes(item.bytes_downloaded)}
                  </td>
                  <td className="mono">{formatNumber(item.files_count)}</td>
                  <td className="mono">{formatSpeed(item.speed_bps)}</td>
                </tr>
              ))}
            </DataTable>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        ) : (
          <EmptyState>ЗАГРУЗОК В ВЫБРАННОМ ПЕРИОДЕ НЕТ</EmptyState>
        )}
      </article>
    </div>
  );
}
